import type React from 'react';

import { Translate } from '@change-corgi/core/react/i18n';
import { Link } from '@change-corgi/design-system-legacy/components/actions';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { getSocialColor, getSocialIcon } from '@change-corgi/design-system-legacy/components/social';
import { Box, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading } from '@change-corgi/design-system-legacy/typography';

import type { RedditShareRecommendations } from 'src/app/pages/starterDashboardV2/shared/hooks';

import { useShareRecommendationsTracking } from '../../hooks/useShareRecommendationsTracking';
import type { Props as MainProps } from '../../index';
import { FeedbackVoter } from '../FeedbackVoter';

type Props = {
	shareRecommendations: RedditShareRecommendations;
	viewContext: MainProps['viewContext'];
};

export const RedditSubreddits: React.FC<Props> = ({ shareRecommendations, viewContext }) => {
	const {
		actions: { handleLinkClick },
	} = useShareRecommendationsTracking();

	if (!shareRecommendations.length) {
		return null;
	}

	return (
		<Box pt={[24, 0]} pb={[12, 0]} sx={{ position: 'relative' }}>
			<Box pb={[16, 4]}>
				<Heading
					as="h3"
					size={['h5']}
					sx={{ lineHeight: '28px', display: 'flex' }}
					id="reddit-recommendations-title"
					data-qa="recommendations-title"
				>
					<Icon
						icon={getSocialIcon('reddit')}
						size={24}
						backgroundColor={getSocialColor('reddit')}
						color="white"
						m={2}
						sx={{ lineHeight: '28px' }}
						mr={8}
						className="rounded"
					/>
					<Translate value="corgi.petition.starter_dashboard.recommendations.reddit.title" />
				</Heading>
			</Box>
			<Box as="ul" role="list" data-testid="reddit-subreddits-list">
				{shareRecommendations.map((subreddit) => (
					<Flex
						as="li"
						role="listitem"
						p={0}
						m={0}
						pt={[0, 12]}
						pb={[12, 0]}
						sx={{ alignItems: 'center', width: '100%', justifyContent: 'space-between', display: 'flex' }}
						key={subreddit.name}
					>
						<Link
							data-qa="share-recommendation-reddit-link"
							to={subreddit.url}
							variant="primary"
							target="_blank"
							sx={{
								fontSize: '16px',
								lineHeight: '24px',
								maxWidth: ['auto', '282px'],
								minWidth: '200px',
								textWrap: 'wrap',
								display: 'flex',
								flexGrow: 4,
							}}
							mr={24}
							onClick={() => handleLinkClick(subreddit.name, 'reddit_group', subreddit.metadata.source, viewContext)}
						>
							{subreddit.name}
						</Link>
						<FeedbackVoter recommendation={subreddit} type="reddit" />
					</Flex>
				))}
			</Box>
		</Box>
	);
};
